import { bootstrapApplication } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { configureFirebase, splashDidHide, splashWillHold } from '@huddle-sports-platform/app-core';
import { initTheme } from '@huddle-sports-platform/ui';

import { appConfig } from './app/app.config';
import { App } from './app/app';
import { holdClubChrome } from './app/core/club-chrome';
import { normaliseClubPath } from './app/core/club-slug';
import { awaitCrest } from './app/core/crest';
import { WHITE_LABEL } from './app/core/whitelabel';
import { environment } from './environments/environment';

// Path form only (a club whose subdomain is not wired up yet):
// `/dublinraiders` -> `/dublinraiders/` before the router reads the URL,
// because a base href only strips a path that carries the trailing slash. A
// club on its own subdomain needs none of this (see app/core/club-slug).
normaliseClubPath();

// A club's page must never wear Huddle's chrome, not even for the moment
// its brand takes to resolve: blank the tab's icon and title now, and the
// brand fills them in (see app/core/club-chrome).
holdClubChrome();

configureFirebase(environment); // the ONE Firebase bootstrap - before anything injects
initTheme({ keyboard: Keyboard }); // stored theme before first paint - no flash of the wrong theme

/** The splash's minimum time on screen; the launch never feels shorter. */
const SPLASH_MINIMUM_MS = 2000;
const launchedAt = Date.now();

// Native: the splash is up - the boot gate veil knows it is covered (a warm
// boot drops it instantly, unseen) and reveal-keyed entrances wait for the
// splash to hide.
if (Capacitor.isNativePlatform()) splashWillHold();

// Start the crest decoding NOW, before bootstrap - by the time the splash's
// minimum beat is served it is almost always ready, so holding for it costs
// nothing. A branded build's crest is baked into the bundle (see
// whitelabel/crest.mjs); this is the belt to that braces.
const crest = awaitCrest(WHITE_LABEL?.logoUrl);

/**
 * Take the splash down - ONCE, whatever happened behind it.
 *
 * `launchAutoHide: false` hands this script the only key: nothing on the
 * native side will ever lower the splash by itself. So every path that ends
 * the launch has to call this, INCLUDING the failures - a bootstrap that
 * rejects, a lazy chunk that will not load, a plugin that throws. Each one
 * otherwise leaves the brand lockup up for ever with no way to see the
 * error underneath, which is indistinguishable from a hung app.
 */
let splashDown = false;
function hideSplash(): void {
  if (splashDown || !Capacitor.isNativePlatform()) return;
  splashDown = true;
  void import('@capacitor/splash-screen')
    .then(({ SplashScreen }) => SplashScreen.hide())
    // Release the reveal-keyed choreography even if the plugin refused: the
    // boot gate waits on splashHidden, and a stuck flag strands the UI.
    .catch((err) => console.error('splash hide failed', err))
    .finally(() => splashDidHide());
}

/**
 * The LAST resort. If the app has not booted within this, show whatever is
 * underneath - an error, a blank page, anything the console can explain -
 * rather than a brand screen that never moves.
 */
const SPLASH_MAXIMUM_MS = 12_000;
if (Capacitor.isNativePlatform()) setTimeout(hideSplash, SPLASH_MAXIMUM_MS);

bootstrapApplication(App, appConfig)
  .then(() => {
    // The native splash holds (SplashScreen.launchAutoHide: false in
    // capacitor.config.ts) until the first themed frame has rendered, so
    // the lockup dissolves straight into the app with no unpainted gap.
    // It also holds a MINIMUM beat - the lockup is a brand moment, not a
    // loading flicker - measured from script start, so the floor only
    // pads launches that would have been shorter. Lazy import: a web
    // no-op, like haptics.
    const remaining = Math.max(0, SPLASH_MINIMUM_MS - (Date.now() - launchedAt));
    const beat = new Promise((resolve) => setTimeout(resolve, remaining));
    // The splash lifts when BOTH are true: the beat is served and the club's
    // crest is ready to paint. Lifting first would show a door with a hole
    // where the crest goes, filling in a second later. `crest` can never
    // outlast its own timeout, so this cannot wedge the launch - and if it
    // somehow did, the maximum above still gets the user out.
    void Promise.all([beat, crest]).catch(() => undefined).then(hideSplash);
  })
  .catch((err) => {
    // A bootstrap that never resolves is exactly what a hung splash looks
    // like from the outside. Get out of the way so the error is visible.
    console.error('bootstrap failed', err);
    hideSplash();
  });
